import { createSelector } from "@reduxjs/toolkit";
import { initialState } from "./slice";

const selectDomain = (state) => state.reportFilter || initialState;
const projectSort = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export const selectClients = createSelector([selectDomain], (state) =>
  state.clients?.map((client) => ({
    value: client._id,
    label: client.companyName,
  })).sort((a, b) => a.label.localeCompare(b.label))
);

export const selectProjects = createSelector([selectDomain], (state) =>
  state.projects?.map((project) => ({
    value: project._id,
    label: project.projectCode,
  })).sort((a, b) => projectSort(a.label, b.label))
);

export const selectCommodities = createSelector([selectDomain], (state) =>
  state.commodities?.map((commodity) => ({
    value: commodity._id,
    label: commodity.name,
  })).sort((a, b) => a.label.localeCompare(b.label))
);

export const selectStates = createSelector([selectDomain], (state) =>
  state.states?.map((state) => ({
    value: state._id,
    label: state.name,
  })).filter(state => !["Narora","Natwar","Paschim Medinipur"].includes(state.label))
  .sort((a, b) => a.label.localeCompare(b.label))
);

export const selectDistricts = createSelector([selectDomain], (state) =>
  state.districts?.map((district) => ({
    value: district._id,
    label: district.city,
  })).sort((a, b) => a.label.localeCompare(b.label))
);

export const selectSamplers = createSelector([selectDomain], (state) =>
  state.samplers?.map((sampler) => ({
    value: sampler._id,
    label: sampler.name,
  })).sort((a, b) => a.label.localeCompare(b.label))
);

// New selector to get raw projects data for filtering logic
export const selectRawProjects = createSelector(
  [selectDomain],
  (state) => state.projects || []
);

// New selector to get raw districts data for filtering logic
export const selectRawDistricts = createSelector(
  [selectDomain],
  (state) => state.districts || []
);

// New selector to get raw samplers data for filtering logic
export const selectRawSamplers = createSelector(
  [selectDomain],
  (state) => state.samplers || []
);

// New selector to get raw labs data for filtering logic
export const selectRawLabs = createSelector(
  [selectDomain],
  (state) => state.labs || []
);

// Selector for projects loading state
export const selectProjectsLoading = createSelector(
  [selectDomain],
  (state) => state.projectsLoading || false
);

export const selectLabs = createSelector([selectDomain], (state) =>
  (state.labs || []).map((lab) => ({
    value: lab._id,
    label: lab.name,
    operatingStates: lab.operatingStates.map((state) => state._id), // for filtering
  })).sort((a, b) => a.label.localeCompare(b.label))
);

// Selector to get filtered labs based on selected states
export const selectFilteredLabs = (selectedStates = []) =>
  createSelector([selectLabs], (labs) => {
    if (!selectedStates || selectedStates.length === 0) {
      return labs || [];
    }

    return (labs || []).filter((lab) => {
      // Show lab if it has at least one operating state that matches selected states
      return lab.operatingStates.some((stateId) =>
        selectedStates.includes(stateId)
      );
    });
  });

// Helper function to filter valid projects based on selected clients
export const getValidProjects = (
  selectedProjects,
  selectedClients,
  allProjects
) => {
  if (!selectedClients || selectedClients.length === 0) {
    return [];
  }

  if (!allProjects || allProjects.length === 0) {
    return selectedProjects;
  }

  const validProjectIds = allProjects
    .filter((project) => {
      const clientId = project.companyId?._id || project.companyId;
      return selectedClients.includes(clientId);
    })
    .map((project) => project._id);

  return selectedProjects.filter((projectId) =>
    validProjectIds.includes(projectId)
  );
};

// Helper function to filter valid districts based on selected states
export const selectRawStates = createSelector(
  [selectDomain],
  (state) => state.states || []
);

// Updated getValidDistricts function - use existing states data
export const getValidDistricts = (
  selectedDistricts,
  selectedStateIds,
  allDistricts,
  allStates // Use the existing states data
) => {
  if (!selectedStateIds || selectedStateIds.length === 0) {
    return [];
  }

  if (!allDistricts || allDistricts.length === 0) {
    return selectedDistricts;
  }

  if (!allStates || allStates.length === 0) {
    return selectedDistricts;
  }

  // Get state names for selected state IDs
  const selectedStateNames = allStates
    .filter(state => selectedStateIds.includes(state._id))
    .map(state => state.name);

  // Filter districts that belong to the selected states
  const validDistrictIds = allDistricts
    .filter(district => selectedStateNames.includes(district.stateName))
    .map(district => district._id);

  return selectedDistricts.filter((districtId) =>
    validDistrictIds.includes(districtId)
  );
};

// Helper function to filter valid samplers based on selected states and districts
export const getValidSamplers = (
  selectedSamplers,
  selectedStates,
  selectedDistricts,
  allSamplers
) => {
  if (
    !selectedStates ||
    selectedStates.length === 0 ||
    !selectedDistricts ||
    selectedDistricts.length === 0
  ) {
    return [];
  }

  if (!allSamplers || allSamplers.length === 0) {
    return selectedSamplers;
  }

  const validSamplerIds = allSamplers
    .filter((sampler) => {
      const stateId = sampler.state?._id;
      const districtId = sampler.district?._id;
      return (
        selectedStates.includes(stateId) &&
        selectedDistricts.includes(districtId)
      );
    })
    .map((sampler) => sampler._id);

  return selectedSamplers.filter((samplerId) =>
    validSamplerIds.includes(samplerId)
  );
};

// Helper function to filter valid labs based on selected states
// A lab is valid if it has at least one operating state that is still selected
export const getValidLabs = (selectedLabs, selectedStates, allLabs) => {
  if (!selectedStates || selectedStates.length === 0) {
    return [];
  }

  if (!allLabs || allLabs.length === 0) {
    return selectedLabs;
  }

  const validLabIds = allLabs
    .filter((lab) => {
      // A lab is valid if it has at least one operating state that is in selectedStates
      const operatingStateIds = lab.operatingStates.map((state) => state._id);
      return operatingStateIds.some((stateId) =>
        selectedStates.includes(stateId)
      );
    })
    .map((lab) => lab._id);

  return selectedLabs.filter((labId) => validLabIds.includes(labId));
};
